/**
 * FootnotesBuilder - Builds footnote layout input from editor state.
 *
 * No external side effects, no DOM access, no callbacks.
 * Note: Mutates the blocks passed to ensureFootnoteMarker internally.
 *
 * ## Key Concepts
 *
 * - `data-sd-footnote-number`: A data attribute marking the superscript number
 *   run (e.g., "1") at the start of footnote content. Used to distinguish the
 *   marker from actual footnote text during rendering and selection.
 *
 * The synthetic marker is visual chrome, not part of the editable note story.
 * It must not carry `pmStart`/`pmEnd`, otherwise the rendered marker consumes
 * horizontal space that the hidden story editor does not own. That creates
 * caret drift and inaccurate click-to-position at the start of the note.
 *
 * @module presentation-editor/layout/FootnotesBuilder
 */

import type { EditorState } from 'prosemirror-state';
import type { FlowBlock, TrackChangeAuthorColorResolver } from '@superdoc/contracts';
import { toFlowBlocks } from '@core/layout-adapter';
import type { ConverterContext } from '@core/layout-adapter/converter-context.js';
import { SUBSCRIPT_SUPERSCRIPT_SCALE } from '@core/layout-adapter/constants.js';
import { formatFootnoteCardinal } from '@core/layout-adapter/footnote-formatting.js';
import { isCustomMarkFollows } from './computeNoteNumbering.js';

import type { ProseMirrorJSON } from '../../types/EditorTypes.js';
import type { FootnoteReference, FootnotesLayoutInput } from '../types.js';
import { findNoteEntryById } from '../../../document-api-adapters/helpers/note-entry-lookup.js';
import { normalizeNotePmJson } from '../../../document-api-adapters/helpers/note-pm-json.js';
import { buildStoryKey } from '../../../document-api-adapters/story-runtime/story-key.js';

// Re-export types for consumers
export type { FootnoteReference, FootnotesLayoutInput };

// =============================================================================
// Types
// =============================================================================

/** Minimal shape of a converter object containing footnote data. */
export type ConverterLike = {
  footnotes?: Array<{ id?: unknown; content?: unknown[] }>;
};

export type NoteRenderOverride = {
  noteId: string;
  docJson: ProseMirrorJSON;
};

/** A text run within a paragraph block. */
type Run = {
  kind?: string;
  text?: string;
  fontFamily?: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  letterSpacing?: number;
  color?: unknown;
  vertAlign?: 'superscript' | 'subscript' | 'baseline';
  baselineShift?: number;
  pmStart?: number | null;
  pmEnd?: number | null;
  dataAttrs?: Record<string, string>;
};

/** Paragraph block with typed runs array. */
type ParagraphBlock = FlowBlock & {
  kind: 'paragraph';
  runs?: Run[];
};

const FOOTNOTE_MARKER_DATA_ATTR = 'data-sd-footnote-number';
const DEFAULT_MARKER_FONT_FAMILY = 'Arial';
const DEFAULT_MARKER_FONT_SIZE = 12;

// =============================================================================
// Public API
// =============================================================================

/**
 * Builds footnote layout input from editor state and converter data.
 *
 * Traverses the document to find footnote references, then builds layout
 * blocks for each referenced footnote with superscript markers prepended.
 *
 * No external side effects, no DOM access, no callbacks.
 * Note: Mutates blocks internally when adding footnote markers.
 *
 * @param editorState - The ProseMirror editor state
 * @param converter - Converter with footnote data
 * @param converterContext - Context with footnote numbering info
 * @param themeColors - Theme colors for styling
 * @returns FootnotesLayoutInput if footnotes exist, null otherwise
 */
export function buildFootnotesInput(
  editorState: EditorState | null | undefined,
  converter: ConverterLike | null | undefined,
  converterContext: ConverterContext | undefined,
  themeColors: unknown,
  renderOverride: NoteRenderOverride | null = null,
  resolveTrackedChangeColor?: TrackChangeAuthorColorResolver,
): FootnotesLayoutInput | null {
  if (!editorState) return null;

  const footnoteNumberById = converterContext?.footnoteNumberById;
  const footnoteNumberFormat = converterContext?.footnoteNumberFormat;
  const footnoteFormatById = converterContext?.footnoteFormatById;
  const importedFootnotes = Array.isArray(converter?.footnotes) ? converter.footnotes : [];

  if (importedFootnotes.length === 0) return null;

  // Find footnote references in the document
  const refs: FootnoteReference[] = [];
  const idsInUse = new Set<string>();
  // SD-2658: customMark footnotes have no w:footnoteRef in note content — skip injection.
  const customMarkIds = new Set<string>();

  editorState.doc.descendants((node, pos) => {
    if (node.type?.name !== 'footnoteReference') return;
    const id = node.attrs?.id;
    if (id == null) return;
    const key = String(id);
    // Use pos + 1 to point inside the node rather than at its boundary.
    // This ensures cursor placement lands within the footnote reference.
    const insidePos = Math.min(pos + 1, editorState.doc.content.size);
    refs.push({ id: key, pos: insidePos });
    idsInUse.add(key);
    if (isCustomMarkFollows(node.attrs?.customMarkFollows)) customMarkIds.add(key);
  });

  if (refs.length === 0) return null;

  // Build blocks for each footnote
  const blocksById = new Map<string, FlowBlock[]>();
  const docDefaultRunProps = resolveDocDefaultRunProps(converterContext);

  idsInUse.forEach((id) => {
    try {
      const footnoteDoc = resolveNoteDocJson(id, importedFootnotes, renderOverride);
      if (!footnoteDoc) return;

      const result = toFlowBlocks(footnoteDoc, {
        blockIdPrefix: `footnote-${id}-`,
        storyKey: buildStoryKey({ kind: 'story', storyType: 'footnote', noteId: id }),
        enableRichHyperlinks: true,
        themeColors: themeColors as never,
        converterContext: converterContext as never,
        resolveTrackedChangeColor,
      });

      if (result?.blocks?.length) {
        if (!customMarkIds.has(id)) {
          // §17.11.11 — per-id format from section override wins over document default.
          const numFmtForId = footnoteFormatById?.[id] ?? footnoteNumberFormat;
          ensureFootnoteMarker(result.blocks, id, footnoteNumberById, numFmtForId, docDefaultRunProps);
        }
        blocksById.set(id, result.blocks);
      }
    } catch (_) {
      // Skip malformed footnotes - invalid JSON structure or conversion failure
    }
  });

  if (blocksById.size === 0) return null;

  return {
    refs,
    blocksById,
    gap: 2,
    topPadding: 4,
    dividerHeight: 1,
  };
}

// =============================================================================
// Internal Helpers
// =============================================================================

/**
 * Checks if a run is a footnote number marker.
 *
 * @param run - The run to check
 * @returns True if the run has the footnote marker data attribute
 */
function isFootnoteMarker(run: Run): boolean {
  return Boolean(run.dataAttrs?.[FOOTNOTE_MARKER_DATA_ATTR]);
}

/**
 * Resolves the display number for a footnote.
 * Falls back to 1 if the footnote ID is not in the mapping or invalid.
 *
 * @param id - The footnote ID
 * @param footnoteNumberById - Mapping of footnote IDs to display numbers
 * @returns The display number (1-based)
 */
function resolveDisplayNumber(id: string, footnoteNumberById: Record<string, number> | undefined): number {
  if (!footnoteNumberById || typeof footnoteNumberById !== 'object') return 1;
  const num = footnoteNumberById[id];
  if (typeof num === 'number' && Number.isFinite(num) && num > 0) return num;
  return 1;
}

/**
 * Document default run properties (w:docDefaults), the middle layer of the
 * marker font fallback chain: explicit first-run value → document default →
 * constant. Keeps the marker sized with the document when the note's first
 * paragraph has no sized run yet (e.g., freshly emptied or inserted notes),
 * so font changes resize the marker predictably instead of snapping to the
 * constant.
 */
type DocDefaultRunProps = {
  fontSize?: number;
  fontFamily?: { ascii?: string };
};

function resolveDocDefaultRunProps(converterContext: ConverterContext | undefined): DocDefaultRunProps | undefined {
  return converterContext?.translatedLinkedStyles?.docDefaults?.runProperties as DocDefaultRunProps | undefined;
}

function resolveMarkerFontFamily(firstTextRun: Run | undefined, docDefaults: DocDefaultRunProps | undefined): string {
  if (typeof firstTextRun?.fontFamily === 'string') return firstTextRun.fontFamily;
  const ascii = docDefaults?.fontFamily?.ascii;
  if (typeof ascii === 'string' && ascii.length > 0) return ascii;
  return DEFAULT_MARKER_FONT_FAMILY;
}

function resolveMarkerBaseFontSize(firstTextRun: Run | undefined, docDefaults: DocDefaultRunProps | undefined): number {
  if (
    typeof firstTextRun?.fontSize === 'number' &&
    Number.isFinite(firstTextRun.fontSize) &&
    firstTextRun.fontSize > 0
  ) {
    return firstTextRun.fontSize;
  }

  // docDefaults fontSize is in half-points; runs use px (1pt = 1/0.75 px).
  const halfPoints = docDefaults?.fontSize;
  if (typeof halfPoints === 'number' && Number.isFinite(halfPoints) && halfPoints > 0) {
    return halfPoints / 2 / 0.75;
  }

  return DEFAULT_MARKER_FONT_SIZE;
}

function buildMarkerRun(
  markerText: string,
  firstTextRun: Run | undefined,
  docDefaults: DocDefaultRunProps | undefined,
): Run {
  // Word renders the FootnoteReference rStyle as a plain superscript, independent
  // of the following run's formatting. Inheriting bold/italic/letterSpacing from
  // the first body text run would render "³**NTD**" with a bold marker — visibly
  // wrong vs Word. Trailing NBSP mirrors the literal " " run Word's source emits
  // between <w:footnoteRef/> and the first body text run.
  const markerRun: Run = {
    kind: 'text',
    text: `${markerText}\u00A0`,
    dataAttrs: { [FOOTNOTE_MARKER_DATA_ATTR]: 'true' },
    fontFamily: resolveMarkerFontFamily(firstTextRun, docDefaults),
    fontSize: resolveMarkerBaseFontSize(firstTextRun, docDefaults) * SUBSCRIPT_SUPERSCRIPT_SCALE,
    vertAlign: 'superscript',
  };

  if (firstTextRun?.color != null) markerRun.color = firstTextRun.color;

  return markerRun;
}

function cloneJsonValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function cloneNoteContentJson(content: unknown[]): ProseMirrorJSON[] {
  return cloneJsonValue(content) as ProseMirrorJSON[];
}

function resolveNoteDocJson(
  id: string,
  importedFootnotes: Array<{ id?: unknown; content?: unknown[] }>,
  renderOverride: NoteRenderOverride | null,
): ProseMirrorJSON | null {
  if (renderOverride && renderOverride.noteId === id) {
    return normalizeNotePmJson(cloneJsonValue(renderOverride.docJson));
  }

  const entry = findNoteEntryById(importedFootnotes, id);
  const content = entry?.content;
  if (!Array.isArray(content) || content.length === 0) {
    return null;
  }

  return normalizeNotePmJson({
    type: 'doc',
    content: cloneNoteContentJson(content),
  });
}

function syncMarkerRun(target: Run, source: Run): void {
  target.kind = source.kind;
  target.text = source.text;
  target.dataAttrs = source.dataAttrs;
  target.fontFamily = source.fontFamily;
  target.fontSize = source.fontSize;
  target.bold = source.bold;
  target.italic = source.italic;
  target.letterSpacing = source.letterSpacing;
  target.color = source.color;
  target.vertAlign = source.vertAlign;
  target.baselineShift = source.baselineShift;
  delete target.pmStart;
  delete target.pmEnd;
}

/**
 * Ensures a footnote block has a superscript marker at the start.
 *
 * Word and other editors display footnote content with a leading superscript
 * number rendered as a normal digit with superscript styling. This function
 * prepends that marker to the first paragraph's runs.
 *
 * If a marker already exists, normalizes it back to the synthetic visual-only
 * shape so stale PM ranges do not leak into the active editing surface.
 * Modifies the blocks array in place.
 *
 * @param blocks - Array of FlowBlocks to modify
 * @param id - The footnote ID
 * @param footnoteNumberById - Mapping of footnote IDs to display numbers
 */
function ensureFootnoteMarker(
  blocks: FlowBlock[],
  id: string,
  footnoteNumberById: Record<string, number> | undefined,
  footnoteNumberFormat: string | undefined,
  docDefaults: DocDefaultRunProps | undefined,
): void {
  const firstParagraph = blocks.find((b) => b?.kind === 'paragraph') as ParagraphBlock | undefined;
  if (!firstParagraph) return;

  const runs: Run[] = Array.isArray(firstParagraph.runs) ? firstParagraph.runs : [];
  const displayNumber = resolveDisplayNumber(id, footnoteNumberById);
  // SD-2986/B1: format the cardinal per the document's w:numFmt so the
  // leading marker matches the inline reference (single source of truth).
  const markerText = formatFootnoteCardinal(displayNumber, footnoteNumberFormat);
  const firstTextRun = runs.find((run) => typeof run.text === 'string' && !isFootnoteMarker(run));
  const normalizedMarkerRun = buildMarkerRun(markerText, firstTextRun, docDefaults);

  // Check if marker already exists
  const existingMarker = runs.find(isFootnoteMarker);
  if (existingMarker) {
    syncMarkerRun(existingMarker, normalizedMarkerRun);
    return;
  }

  // Insert marker at the very start of runs
  runs.unshift(normalizedMarkerRun);
  // Cast needed: local Run type is structurally compatible but not identical
  // to the FlowBlock's Run type from @superdoc/contracts
  (firstParagraph as { runs: Run[] }).runs = runs;
}
